import React, { useState, useEffect } from "react";
import { Table, DatePicker, Select, Input, Tag, Spin } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, BookOutlined, EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import axiosInstance from "../../axios";
import { dateDisplayable } from "../table-utils";
import dayjs from "dayjs";

const { Option } = Select;

interface AttendanceTabPanelProps {
  dealings_id: string;
}

export const AttendanceTabPanel: React.FC<AttendanceTabPanelProps> = ({ dealings_id }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    completedClasses: 0,
    cancelledClasses: 0,
    notMarkedClasses: 0,
  });
  const [tuitionInfo, setTuitionInfo] = useState<any>(null);
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [monthYear, setMonthYear] = useState<dayjs.Dayjs | null>(dayjs());
  const [status, setStatus] = useState<string>("All");
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealings_id, pagination.current, pagination.pageSize, monthYear, status]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (pagination.current !== 1) {
        setPagination({ ...pagination, current: 1 });
      } else {
        fetchAttendance();
      }
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchAttendance = async () => {
    if (!dealings_id) return;
    setLoading(true);
    try {
      const params: any = {
        dealings_id,
        page: pagination.current,
        limit: pagination.pageSize,
      };

      if (monthYear) {
        params.month = monthYear.month() + 1; // 1-12
        params.year = monthYear.year();
      }

      if (status !== "All") {
        params.status = status;
      }

      if (search) {
        params.search = search;
      }

      const response = await axiosInstance.get(`/v1/cre/student/deal/attendance`, { params });
      if (response.data?.success || response.data?.is_success) {
        const result = response.data.data;
        setData(result.data);
        setPagination((prev) => ({
          ...prev,
          total: result.meta?.total || 0,
        }));
        setSummary(result.summary || {
          completedClasses: 0,
          cancelledClasses: 0,
          notMarkedClasses: 0,
        });
        if (result.tuition_info) {
          setTuitionInfo(result.tuition_info);
        }
      }
    } catch (error) {
      console.error("Failed to fetch attendance", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(newPagination);
  };

  const columns = [
    {
      title: "Class Date",
      dataIndex: "date",
      key: "date",
      render: (text: string) => dateDisplayable(text),
    },
    {
      title: "Tutor Name",
      dataIndex: "tutor_name",
      key: "tutor_name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        let color = "default";
        let icon = null;
        if (status === "Completed") {
          color = "success";
          icon = <CheckCircleOutlined />;
        } else if (status === "Cancelled") {
          color = "error";
          icon = <CloseCircleOutlined />;
        }
        return (
          <Tag color={color} icon={icon} className="tail-rounded-full tail-px-2 tail-py-0.5">
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Tutor Marked",
      dataIndex: "tutor_marked",
      key: "tutor_marked",
      render: (text: string) => (text ? dayjs(text).format("DD MMM YYYY, hh:mmA") : "-"),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
    },
  ];

  return (
    <div className="tail-flex tail-flex-col tail-gap-4">
      {/* Filters */}
      <div className="tail-flex tail-gap-4 tail-flex-wrap tail-items-center">
        <DatePicker
          picker="month"
          value={monthYear}
          onChange={(date) => {
            setMonthYear(date);
            setPagination({ ...pagination, current: 1 });
          }}
          className="tail-w-48"
          allowClear={true}
        />
        <Select
          value={status}
          onChange={(val) => {
            setStatus(val);
            setPagination({ ...pagination, current: 1 });
          }}
          className="tail-w-48"
        >
          <Option value="All">Status: All</Option>
          <Option value="Completed">Completed</Option>
          <Option value="Cancelled">Cancelled</Option>
          <Option value="Not Marked">Not Marked</Option>
        </Select>
        <Input.Search
          placeholder="Search attendance notes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="tail-w-64 tail-ml-auto"
        />
      </div>

      {/* Tuition Info */}
      {tuitionInfo && (
        <div className="tail-bg-white tail-border tail-border-gray-200 tail-rounded-lg tail-p-4 tail-flex tail-flex-col tail-gap-4">
          <div className="tail-text-2xl tail-font-semibold tail-text-slate-900">
            {tuitionInfo.student_name}
          </div>
          <div className="tail-flex tail-flex-wrap tail-gap-x-8 tail-gap-y-4 tail-items-end">
            <div className="tail-flex tail-flex-col tail-gap-1">
              <span className="tail-text-sm tail-text-slate-500">Start date</span>
              <span className="tail-font-medium tail-text-slate-800">
                {tuitionInfo.start_date ? dayjs(tuitionInfo.start_date).format("DD MMM YYYY") : "-"}
              </span>
            </div>
            
            <div className="tail-flex tail-items-center tail-gap-2 tail-text-slate-600">
              <BookOutlined className="tail-text-slate-400" />
              <span>{tuitionInfo.grade} • {tuitionInfo.subject}</span>
            </div>
            
            <div className="tail-flex tail-items-center tail-gap-2 tail-text-slate-600">
              <EnvironmentOutlined className="tail-text-slate-400" />
              <span>{tuitionInfo.address || "-"}</span>
            </div>
            
            <div className="tail-flex tail-items-center tail-gap-2 tail-text-slate-600">
              <ClockCircleOutlined className="tail-text-slate-400" />
              <span>{tuitionInfo.time || "-"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="tail-grid tail-grid-cols-1 md:tail-grid-cols-3 tail-gap-4">
        <div className="tail-border tail-border-gray-200 tail-rounded-lg tail-p-4 tail-bg-white">
          <div className="tail-flex tail-justify-between tail-items-center tail-mb-2">
            <span className="tail-text-gray-500 tail-text-sm">Completed classes</span>
            <CheckCircleOutlined className="tail-text-green-500" />
          </div>
          <div className="tail-text-3xl tail-font-semibold">{summary.completedClasses}</div>
        </div>
        <div className="tail-border tail-border-gray-200 tail-rounded-lg tail-p-4 tail-bg-white">
          <div className="tail-flex tail-justify-between tail-items-center tail-mb-2">
            <span className="tail-text-gray-500 tail-text-sm">Cancelled Classes</span>
            <CloseCircleOutlined className="tail-text-red-500" />
          </div>
          <div className="tail-text-3xl tail-font-semibold">{summary.cancelledClasses}</div>
        </div>
        <div className="tail-border tail-border-gray-200 tail-rounded-lg tail-p-4 tail-bg-white">
          <div className="tail-flex tail-justify-between tail-items-center tail-mb-2">
            <span className="tail-text-gray-500 tail-text-sm">Not marked classes</span>
            <CloseCircleOutlined className="tail-text-red-500" />
          </div>
          <div className="tail-text-3xl tail-font-semibold">{summary.notMarkedClasses}</div>
        </div>
      </div>

      {/* Table */}
      <div className="tail-bg-white tail-border tail-border-gray-200 tail-rounded-lg tail-overflow-hidden">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};
